import {
	CheckIdResult,
	SendOtpResult,
	VerifyOtpResult,
	VerifyPasswordResult,
} from './SignInV2.api';
import { resolveOtpChannel } from './SignInV2.utils';

/** TODO: set to false and wire the real endpoint once the backend is up. */
export const USE_MOCK_SIGN_IN_V2 = false;

/** password that drives the wrong-password design state */
const WRONG_PASSWORD = '1234';

/** code that drives the wrong-code design state */
const WRONG_CODE = '000000';

/** already masked, like the server sends them - the client only displays them */
const PHONE = '***-****363';
const MAIL = 'r***@michpal.com';

/**
 * Stands in for the signed step token. The mock never checks it - it only keeps
 * the container's token threading exercised while mocked.
 */
const STEP_TOKEN = 'mock-step-token';

/**
 * Deterministic branches so every flow is testable by typing an ID
 * (the ID must still pass the client-side checksum to reach the mock):
 *
 * | ID shape          | result                                          |
 * |-------------------|-------------------------------------------------|
 * | ends with 00      | not registered (valid: false)                   |
 * | ends with 2/4/6/8 | 2FA, phone + mail -> OTP by SMS, voice offered  |
 * | ends with 1       | 2FA, mail only -> OTP by mail, no voice call    |
 * | ends with 3       | 2FA, phone only -> OTP by SMS, voice offered    |
 * | otherwise (5/7/9) | no 2FA -> password screen with one-time-code alt|
 *
 * Password step (2FA branch only):
 * | password          | result                                          |
 * |-------------------|-------------------------------------------------|
 * | 1234              | wrong password                                  |
 * | anything else     | ok -> OTP sent to phone if it exists, else mail |
 *
 * OTP step:
 * | code              | result                                          |
 * |-------------------|-------------------------------------------------|
 * | 000000            | wrong code                                      |
 * | anything else     | ok, but no loginPayload -> cannot log in while  |
 * |                   | mocked (see VerifyOtpResult)                    |
 */
const resolveProfile = (teudatZehut: string): CheckIdResult => {
	const lastDigit = Number(teudatZehut[teudatZehut.length - 1]);
	if (teudatZehut.endsWith('00')) {
		return {
			valid: false,
			requires2fa: false,
			hasPhone: false,
			hasMail: false,
		};
	}
	if (lastDigit % 2 === 0) {
		return {
			valid: true,
			requires2fa: true,
			hasPhone: true,
			hasMail: true,
			phone: PHONE,
			mail: MAIL,
		};
	}
	// mail only
	if (lastDigit === 1) {
		return {
			valid: true,
			requires2fa: true,
			hasPhone: false,
			hasMail: true,
			mail: MAIL,
		};
	}
	// phone only
	if (lastDigit === 3) {
		return {
			valid: true,
			requires2fa: true,
			hasPhone: true,
			hasMail: false,
			phone: PHONE,
		};
	}
	return {
		valid: true,
		requires2fa: false,
		hasPhone: true,
		hasMail: true,
		phone: PHONE,
		mail: MAIL,
	};
};

export const mockCheckId = (teudatZehut: string): Promise<CheckIdResult> =>
	new Promise(resolve =>
		setTimeout(() => {
			const profile = resolveProfile(teudatZehut);
			// an invalid id gets no token, exactly like the server
			resolve(
				profile.valid ? { ...profile, stepToken: STEP_TOKEN } : profile,
			);
		}, 1000),
	);

export const mockVerifyPassword = (
	teudatZehut: string,
	password: string,
): Promise<VerifyPasswordResult> =>
	new Promise(resolve =>
		setTimeout(() => {
			if (password === WRONG_PASSWORD) {
				// no new token on a wrong password, like the server
				resolve({ ok: false });
				return;
			}
			resolve({
				ok: true,
				sentTo: resolveOtpChannel(resolveProfile(teudatZehut)),
				stepToken: STEP_TOKEN,
			});
		}, 1000),
	);

export const mockSendOtp = (teudatZehut: string): Promise<SendOtpResult> =>
	new Promise(resolve =>
		setTimeout(() => {
			const profile = resolveProfile(teudatZehut);
			resolve({ sentTo: resolveOtpChannel(profile), stepToken: STEP_TOKEN });
		}, 1000),
	);

export const mockVerifyOtp = (
	teudatZehut: string,
	code: string,
): Promise<VerifyOtpResult> =>
	new Promise(resolve =>
		setTimeout(
			() => resolve({ ok: code !== WRONG_CODE }),
			1000,
		),
	);
